import { useEffect, useMemo, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Stack, Typography } from '@mui/material';
import {
  ApartmentOutlined,
  AssignmentOutlined,
  AutoAwesomeOutlined,
  CreditCardOutlined,
  DescriptionOutlined,
  GroupsOutlined,
  HistoryOutlined,
  ManageAccountsOutlined,
  PersonOutlineOutlined,
  PsychologyAltOutlined,
  ReceiptLongOutlined,
  SettingsOutlined,
} from '@mui/icons-material';
import classnames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { IApiRequests, useApiRequests, useSubscription, useUser } from 'hooks';
import { useAppSelector } from 'store';
import { selectUser } from 'store/user';
import { ApiUser, ClinicListItemResponse, ClinicListResponse } from 'types';
import { ProfileSelector } from '../ProfileSelector';
import { MenuState } from 'pages/SettingPage/SettingsPage';
import { getIsAdminByRoles } from 'utils';
import { Spinner, BackButton } from 'components';
import { IUserContext } from 'context';
import { isHipaaLink } from 'config/productProfile';

import styles from './LeftMenu.module.scss';

type LeftMenuProps = {
  menuState: MenuState | null;
  onMenuStateChange(state: MenuState | null): void;
  onLoadingChange?(isLoading: boolean): void;
};

export function LeftMenu({ menuState, onMenuStateChange, onLoadingChange }: LeftMenuProps) {
  const { t } = useTranslation('settings');

  const subscription = useSubscription();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const user: ApiUser = useAppSelector(selectUser)!;
  const { getClinicsList }: IApiRequests = useApiRequests();
  const { changeClinic }: IUserContext = useUser();
  const [loading, setLoading] = useState<boolean>(true);
  const [clinicsList, setClinicsList] = useState<ClinicListItemResponse[]>([]);
  const hasLoadedClinicsRef = useRef<boolean>(false);
  const isMember = user?.clinicUser?.via3UserType === 'Member';
  const canAccessFaxes = !!user?.clinicUser?.canAccessFaxes && !isMember;
  const canAccessCallHistory = user?.clinicUser?.enableCallHistory !== false;

  const isAdmin = useMemo<boolean>(() => {
    return getIsAdminByRoles(user?.clinicUser.roles || []);
  }, [user]);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      onLoadingChange?.(false);
      return;
    }

    // Avoid reloading the whole Settings layout whenever user store updates
    // from unrelated actions (e.g. Documents tab activity).
    if (hasLoadedClinicsRef.current) {
      return;
    }

    setLoading(true);
    onLoadingChange?.(true);
    getClinicsList()
      .then(({ clinics }: ClinicListResponse) => {
        setClinicsList(clinics);
        hasLoadedClinicsRef.current = true;
      })
      .finally(() => {
        setLoading(false);
        onLoadingChange?.(false);
      });
  }, [user?.id, onLoadingChange]);

  const handleBackClick = (): void => {
    navigate('../');
  };

  const handleClinicChange = (clinicId: string): Promise<void> => {
    if (clinicId === user?.clinicId) {
      return Promise.resolve();
    }

    setLoading(true);
    onLoadingChange?.(true);

    return changeClinic(clinicId)
      .then(() => Promise.resolve())
      .catch((error: any) => {
        enqueueSnackbar(t('leftMenu.changeClinicError', { error }), { variant: 'error' });
      })
      .finally(() => {
        setLoading(false);
        onLoadingChange?.(false);
      });
  };

  const handleProfileClick = (): void => {
    onMenuStateChange('profile');
  };

  const handleAccountSettingsClick = (): void => {
    onMenuStateChange('accountSettings');
  };

  const handleYourOrganizationClick = (): void => {
    onMenuStateChange('yourOrganization');
  };

  const handleStaffClick = (): void => {
    onMenuStateChange('staff');
  };

  const handleCallHistoryClick = (): void => {
    onMenuStateChange('callHistory');
  };

  const handleAINotesClick = (): void => {
    onMenuStateChange('aiNotes');
  };

  const handleDocumentsClick = (): void => {
    onMenuStateChange('documents');
  };

  const handleFaxesClick = (): void => {
    onMenuStateChange('faxes');
  };

  const handleNotesGeneratorClick = (): void => {
    onMenuStateChange('notesGenerator');
  };

  const handleAcceptPaymentsClick = (): void => {
    onMenuStateChange('acceptPayments');
  };

  const handleSubscriptionsClick = (): void => {
    onMenuStateChange('subscriptions');
  };

  const handleTeleconsentClick = (): void => {
    onMenuStateChange('teleconsent');
  };

  return loading ? (
    <Spinner />
  ) : (
    <div className={styles.leftMenu}>
      {(!isAdmin || (isAdmin && subscription?.hasSubscription && subscription.isSubscriptionActive)) && (
        <BackButton text={t('leftMenu.backToRoom')} onClick={handleBackClick} />
      )}

      <Stack className={styles.main}>
        <Stack className={styles.profileMenu}>
          <ProfileSelector clinicsList={clinicsList} onChangeClinic={handleClinicChange} />

          <Stack
            className={classnames(styles.item, {
              [styles.itemSelected]: menuState === 'profile',
            })}
            onClick={handleProfileClick}
          >
            <Typography variant='body2' className={styles.itemLabel}>
              <PersonOutlineOutlined className={styles.itemIcon} />
              {t('leftMenu.myProfile')}
            </Typography>
          </Stack>

          <Stack
            className={classnames(styles.item, {
              [styles.itemSelected]: menuState === 'accountSettings',
            })}
            onClick={handleAccountSettingsClick}
          >
            <Typography variant='body2' className={styles.itemLabel}>
              <SettingsOutlined className={styles.itemIcon} />
              {t('leftMenu.accountSecurity')}
            </Typography>
          </Stack>
        </Stack>

        <Stack className={styles.organizationMenu}>
          <Typography className={styles.sectionTitle}>{t('leftMenu.organization')}</Typography>

          <Stack
            className={classnames(styles.item, {
              [styles.itemSelected]: menuState === 'yourOrganization',
            })}
            onClick={handleYourOrganizationClick}
          >
            <Typography variant='body2' className={styles.itemLabel}>
              <ApartmentOutlined className={styles.itemIcon} />
              {t('leftMenu.organizationDetails')}
            </Typography>
          </Stack>

          {isAdmin && (
            <Stack
              className={classnames(styles.item, {
                [styles.itemSelected]: menuState === 'staff',
              })}
              onClick={handleStaffClick}
            >
              <Typography variant='body2' className={styles.itemLabel}>
                <GroupsOutlined className={styles.itemIcon} />
                {t('leftMenu.staff')}
              </Typography>
            </Stack>
          )}

          {canAccessCallHistory && (
            <Stack
              className={classnames(styles.item, {
                [styles.itemSelected]: menuState === 'callHistory',
              })}
              onClick={handleCallHistoryClick}
            >
              <Typography variant='body2' className={styles.itemLabel}>
                <HistoryOutlined className={styles.itemIcon} />
                {t('leftMenu.callHistory')}
              </Typography>
            </Stack>
          )}
        </Stack>

        <Stack className={styles.featuresMenu}>
          <Typography className={styles.sectionTitle}>{t('leftMenu.features')}</Typography>

          <Stack
            className={classnames(styles.item, {
              [styles.itemSelected]: menuState === 'documents',
            })}
            onClick={handleDocumentsClick}
          >
            <Typography variant='body2' className={styles.itemLabel}>
              <DescriptionOutlined className={styles.itemIcon} />
              {t('leftMenu.documents')}
            </Typography>
          </Stack>

          {isHipaaLink && (
            <Stack
              className={classnames(styles.item, {
                [styles.itemSelected]: menuState === 'notesGenerator',
              })}
              onClick={handleNotesGeneratorClick}
            >
              <Typography variant='body2' className={styles.itemLabel}>
                <AutoAwesomeOutlined className={styles.itemIcon} />
                {t('leftMenu.notesGenerator')}
                <span className={styles.newBadge}>{t('leftMenu.new')}</span>
              </Typography>
            </Stack>
          )}

          {isHipaaLink && (
            <Stack
              className={classnames(styles.item, {
                [styles.itemSelected]: menuState === 'aiNotes',
              })}
              onClick={handleAINotesClick}
            >
              <Typography variant='body2' className={styles.itemLabel}>
                <PsychologyAltOutlined className={styles.itemIcon} />
                {t('leftMenu.aiProgressNotes')}
              </Typography>
            </Stack>
          )}

          {isHipaaLink && (
            <Stack
              className={classnames(styles.item, {
                [styles.itemSelected]: menuState === 'acceptPayments',
              })}
              onClick={handleAcceptPaymentsClick}
            >
              <Typography variant='body2' className={styles.itemLabel}>
                <CreditCardOutlined className={styles.itemIcon} />
                {t('leftMenu.acceptPayments')}
              </Typography>
            </Stack>
          )}

          {!isHipaaLink && canAccessFaxes && (
            <Stack
              className={classnames(styles.item, {
                [styles.itemSelected]: menuState === 'faxes',
              })}
              onClick={handleFaxesClick}
            >
              <Typography variant='body2' className={styles.itemLabel}>
                <ManageAccountsOutlined className={styles.itemIcon} />
                {t('leftMenu.faxes')}
              </Typography>
            </Stack>
          )}

          {isHipaaLink && isAdmin && (
            <Stack
              className={classnames(styles.item, {
                [styles.itemSelected]: menuState === 'teleconsent',
              })}
              onClick={handleTeleconsentClick}
            >
              <Typography variant='body2' className={styles.itemLabel}>
                <AssignmentOutlined className={styles.itemIcon} />
                {t('leftMenu.teleconsent')}
              </Typography>
            </Stack>
          )}
        </Stack>

        {isHipaaLink && (
          <Stack className={styles.billingMenu}>
            <Typography className={styles.sectionTitle}>{t('leftMenu.billing')}</Typography>

            <Stack
              className={classnames(styles.item, {
                [styles.itemSelected]: menuState === 'subscriptions',
              })}
              onClick={handleSubscriptionsClick}
            >
              <Typography variant='body2' className={styles.itemLabel}>
                <ReceiptLongOutlined className={styles.itemIcon} />
                {t('leftMenu.subscriptions')}
              </Typography>
            </Stack>
          </Stack>
        )}
      </Stack>
    </div>
  );
}
